import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { IntelligenceEventType, IntelligenceStreamEvent } from '../../../common/api/clients/intelligence.api';

interface IntelligenceResult {
    success: boolean;
}

export interface UseIntelligenceStreamResult<
    TResult extends IntelligenceResult,
    TEvent extends IntelligenceStreamEvent<TResult>,
> {
    events: TEvent[];
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: Error | null;
    startStream: () => void;
    refetch: () => void;
    finalResult?: TResult;
    setCompletedCallback: (callback: () => void) => void;
}

interface UseIntelligenceStreamConfig<
    TResult extends IntelligenceResult,
    TEvent extends IntelligenceStreamEvent<TResult>,
> {
    queryKey: readonly unknown[];
    streamFunction: (
        orderId: string,
        onEvent: (event: TEvent) => void,
        onError: (error: Error) => void,
        onComplete: () => void
    ) => Promise<void>;
    streamName: string;
}

export const useIntelligenceStream = <
    TResult extends IntelligenceResult,
    TEvent extends IntelligenceStreamEvent<TResult>,
>(
    orderId: string,
    config: UseIntelligenceStreamConfig<TResult, TEvent>
): UseIntelligenceStreamResult<TResult, TEvent> => {
    const queryClient = useQueryClient();
    const streamingRef = useRef(false);
    const [error, setError] = useState<Error | null>(null);
    const completedCallbackRef = useRef<() => void>(() => {});

    const { data: events = [], refetch } = useQuery<TEvent[]>({
        queryKey: config.queryKey,
        queryFn: () => Promise.resolve([]),
        enabled: false,
        initialData: [],
    });

    const startStream = useCallback(() => {
        if (streamingRef.current) return;

        streamingRef.current = true;
        setError(null);

        queryClient.setQueryData(config.queryKey, []);

        const onEvent = (event: TEvent) => {
            queryClient.setQueryData(config.queryKey, (oldEvents: TEvent[] = []) => [...oldEvents, event]);

            if (event.event === IntelligenceEventType.COMPLETED) {
                streamingRef.current = false;
            }
        };

        const onError = (error: Error) => {
            Sentry.captureException(error);
            setError(error);
            streamingRef.current = false;
        };

        const onComplete = () => {
            streamingRef.current = false;
            completedCallbackRef.current();
        };

        try {
            config.streamFunction(orderId, onEvent, onError, onComplete);
        } catch (syncError) {
            Sentry.captureException(syncError);
            setError(syncError as Error);
            streamingRef.current = false;
        }
    }, [queryClient, config, orderId]);

    const finalResult = events.find((event) => event.event === IntelligenceEventType.COMPLETED)?.result;
    const successUpdateEvent = events.find((event) => event.event === IntelligenceEventType.SUCCESS_UPDATE);
    const hasSuccessUpdate = !!successUpdateEvent;
    const successUpdateIsSuccess = successUpdateEvent?.isSuccess || false;

    const isStreamingActive = streamingRef.current;
    const hasFinalResult = !!finalResult;
    const finalResultIsSuccess = finalResult?.success || false;

    const isInitialLoadingPhase = isStreamingActive && !hasSuccessUpdate;
    const isWaitingForFinalResult = hasSuccessUpdate && !hasFinalResult && successUpdateIsSuccess;
    const isLoading = isInitialLoadingPhase || isWaitingForFinalResult;

    const isSuccess = hasFinalResult ? finalResultIsSuccess : successUpdateIsSuccess;

    const setCompletedCallback = useCallback((callback: () => void) => {
        completedCallbackRef.current = callback;
    }, []);

    return {
        events,
        isLoading,
        isSuccess,
        isError: !!error,
        error,
        startStream,
        refetch: () => refetch(),
        finalResult,
        setCompletedCallback,
    };
};
